import { withListOverflow } from "../../v2/components/ListOverflow.jsx";
export const RtoComplianceReport = withListOverflow(RtoComplianceListOwner);
import React, { useEffect, useMemo, useState } from "react";
import { getRtoComplianceMonthly, getRtoComplianceQuarterly, getRtoComplianceWeekly } from "../../data/reportsClient.js";
import { T } from "../../app/tokens.js";
import { ReportShell } from "./ReportShell.jsx";
import { ReportAccessNotice } from "./ReportShell.jsx";
import { useFeatureFlagContext } from "../../contexts/FeatureFlagContext.jsx";
import { getReportNavEligibility } from "./reportNavEligibility.js";

const FULL_TIME = "Full time";
const EMPLOYEE = "Employee";
const PERIOD_WORKER_CLASSES = [EMPLOYEE, "Vendor Contractor", "Independent Contractor"];
const OVER_DENOMINATOR_NOTE = "Badged count exceeded Workday headcount for this period - verify identity coverage.";

function pct(value) {
  return value == null ? "—" : `${Number(value).toFixed(1)}%`;
}

function number(value) {
  return value == null ? "—" : Number(value).toLocaleString();
}

function statusColor(value) {
  if (value == null) return T.light;
  if (value >= 80) return T.green;
  if (value >= 60) return T.amber;
  return T.red;
}

function Status({ row }) {
  if (row.status === "coverage_gap") {
    return (
      <span style={{ display: "inline-flex", flexDirection: "column", gap: 3, color: T.light, fontWeight: T.fwSemibold }}>
        <span>No Workday headcount</span>
      </span>
    );
  }
  if (row.status === "no_data") {
    return <span style={{ color: T.light, fontWeight: T.fwSemibold }}>No data</span>;
  }
  return (
    <span style={{ color: statusColor(row.compliancePct), fontWeight: T.fwBold }}>
      {pct(row.compliancePct)}
      {row.status === "observed_zero" && (
        <span title="Observed zero: Workday headcount was present and the measured result was 0%; this is not a closure or missing Workday headcount." style={{ color: T.amber, marginLeft: 5 }}>!</span>
      )}
      {row.status === "exceeds_denominator" && (
        <span
          data-testid={`rto-overdenominator-indicator-${row.market}`}
          title={OVER_DENOMINATOR_NOTE}
          aria-label={OVER_DENOMINATOR_NOTE}
          style={{ color: T.amber, marginLeft: 5, cursor: "help" }}
        >
          !
        </span>
      )}
    </span>
  );
}

function Kpi({ label, value, detail, tone }) {
  return (
    <div style={{
      background: T.card, border: `1px solid ${T.border}`, borderRadius: 12,
      padding: "16px 18px", minWidth: 0,
    }}>
      <div style={{ color: T.light, fontSize: T.fsXs, fontWeight: T.fwBold, textTransform: "uppercase", letterSpacing: ".06em" }}>{label}</div>
      <div style={{ color: tone || T.dark, fontSize: T.fsXl, fontWeight: T.fwDisplay, marginTop: 8 }}>{value}</div>
      {detail && <div style={{ color: T.mid, fontSize: T.fsXs, marginTop: 5 }}>{detail}</div>}
    </div>
  );
}

function PeriodControl({ mode, data, onChange }) {
  const label = mode === "month" ? "Month" : mode === "quarter" ? "Quarter" : "Week";
  return (
    <label style={{ display: "flex", alignItems: "center", gap: 8, color: T.mid, fontSize: T.fsSm }}>
      {label}
      <select
        data-testid={`rto-${mode}-select`}
        value={data?.period || ""}
        onChange={(e) => onChange(e.target.value)}
        style={{ border: `1px solid ${T.border}`, borderRadius: 7, padding: "7px 9px", color: T.dark, background: T.card, fontSize: T.fsSm5 }}
      >
        {(data?.availablePeriods || []).map((period) => (
          <option key={period.value} value={period.value}>
            {period.value}{period.isComplete ? "" : " · partial"}
          </option>
        ))}
      </select>
    </label>
  );
}

function readableRtoPeriod(mode, period) {
  if (!period) return "No reporting period";
  if (mode === "month") {
    const [year, month] = period.split("-").map(Number);
    return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString("en-US", {
      month: "long", year: "numeric", timeZone: "UTC",
    });
  }
  if (mode === "quarter") {
    const match = /^(\d{4})-Q([1-4])$/.exec(period);
    return match ? `Q${match[2]} ${match[1]}` : period;
  }
  const date = new Date(`${period}T00:00:00Z`);
  return Number.isNaN(date.getTime())
    ? period
    : `Week of ${date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" })}`;
}

function Trend({ history = [], market }) {
  const points = history.filter((item) => item.market === market).slice(0, 6).reverse();
  if (points.length < 2) return <span style={{ color: T.light }}>—</span>;
  return (
    <span data-testid={`rto-trend-${market}`} style={{ display: "inline-flex", alignItems: "end", gap: 2, height: 20 }}>
      {points.map((point, index) => (
        <span
          key={`${point.period}-${index}`}
          title={`${point.period}: ${pct(point.compliancePct)}`}
          style={{ width: 5, height: `${Math.max(3, Math.min(20, (point.compliancePct || 0) / 5))}px`, background: statusColor(point.compliancePct), borderRadius: 2 }}
        />
      ))}
    </span>
  );
}

const MARKET_SORTS = {
  month: [
    { key: "eligible", label: "Eligible" },
    { key: "met", label: "Met" },
    { key: "compliancePct", label: "Compliance %" },
    { key: "dailyAvgPresent", label: "Avg daily" },
    { key: "dailyPeakPresent", label: "Peak" },
  ],
  quarter: [
    { key: "eligible", label: "Eligible" },
    { key: "met", label: "Met" },
    { key: "compliancePct", label: "Compliance %" },
  ],
  week: [
    { key: "eligible", label: "Eligible" },
    { key: "present", label: "Present" },
    { key: "attendancePct", label: "Attendance %" },
  ],
};

function sortValue(row, key, mode) {
  if (key === "met") return row[mode === "month" ? "compliant10plus" : "compliant30plus"];
  return row[key];
}

function SortHeader({ label, sortKey, sortDirection, onSort }) {
  const active = sortKey === label.key;
  const direction = active ? (sortDirection === "asc" ? "↑" : "↓") : "";
  return (
    <th
      aria-sort={active ? (sortDirection === "asc" ? "ascending" : "descending") : "none"}
      style={{ padding: "11px 14px", borderBottom: `1px solid ${T.border}`, fontWeight: T.fwBold }}
    >
      <button
        type="button"
        onClick={() => onSort(label.key)}
        aria-label={`Sort by ${label.label}`}
        style={{
          display: "inline-flex", alignItems: "center", gap: 5, padding: 0,
          border: 0, background: "transparent", color: "inherit", font: "inherit",
          textTransform: "inherit", letterSpacing: "inherit", cursor: "pointer",
        }}
      >
        {label.label}
        <span aria-hidden="true" style={{ color: active ? T.blue : T.light, minWidth: 8 }}>{direction}</span>
      </button>
    </th>
  );
}

function sortedRows(rows, sortKey, sortDirection, mode) {
  return rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => {
      const av = sortValue(a.row, sortKey, mode);
      const bv = sortValue(b.row, sortKey, mode);
      const aMissing = av == null || Number.isNaN(Number(av));
      const bMissing = bv == null || Number.isNaN(Number(bv));
      if (aMissing !== bMissing) return aMissing ? 1 : -1;
      if (!aMissing && Number(av) !== Number(bv)) {
        const comparison = Number(av) - Number(bv);
        return sortDirection === "asc" ? comparison : -comparison;
      }
      return a.index - b.index;
    })
    .map(({ row }) => row);
}

function MarketTable({ data, mode, sortKey, sortDirection, onSort }) {
  const isWeekly = mode === "week";
  const headlineClass = isWeekly ? FULL_TIME : EMPLOYEE;
  const rows = (data?.rows || []).filter((row) => row.workerClass === headlineClass);
  const thresholdField = mode === "month" ? "compliant10plus" : "compliant30plus";
  const headers = MARKET_SORTS[mode];
  const orderedRows = useMemo(
    () => sortedRows(rows, sortKey, sortDirection, mode),
    [rows, sortKey, sortDirection, mode],
  );
  return (
    <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, overflow: "hidden" }}>
      <div style={{ padding: "15px 18px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <div>
          <div style={{ color: T.dark, fontWeight: T.fwBold, fontSize: T.fsMd5 }}>{isWeekly ? "Full-time attendance" : "Market compliance"}</div>
          <div style={{ color: T.light, fontSize: T.fsXs, marginTop: 3 }}>
            {isWeekly ? "Weekly attendance by market" : `${headlineClass} · ${mode === "month" ? "10+ days" : "30+ days"}`}
          </div>
        </div>
        <span style={{ color: T.light, fontSize: T.fsXs }}>{data?.isComplete ? "Complete period" : "Partial period"}</span>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: T.fsSm5 }}>
          <thead>
            <tr style={{ color: T.light, textAlign: "left", fontSize: T.fsXs, textTransform: "uppercase", letterSpacing: ".04em" }}>
              <th style={{ padding: "11px 14px", borderBottom: `1px solid ${T.border}`, fontWeight: T.fwBold }}>Market</th>
              {headers.map((header) => <SortHeader key={header.key} label={header} sortKey={sortKey} sortDirection={sortDirection} onSort={onSort} />)}
              {!isWeekly && <th style={{ padding: "11px 14px", borderBottom: `1px solid ${T.border}`, fontWeight: T.fwBold }}>Trend</th>}
            </tr>
          </thead>
          <tbody>
            {orderedRows.map((row) => {
              const muted = row.status === "coverage_gap";
              const cellColor = muted ? T.light : T.mid;
              return (
              <tr key={row.market} data-testid={`rto-market-row-${row.market}`} style={{ borderBottom: `1px solid ${T.border}`, background: muted ? T.lightGray : T.card }}>
                <td style={{ padding: "13px 14px", color: muted ? T.light : T.dark, fontWeight: T.fwSemibold }}>
                  {row.market}
                  {muted && row.note && <div data-testid={`rto-coverage-note-${row.market}`} style={{ marginTop: 3, color: T.light, fontSize: T.fsXs, fontWeight: T.fwRegular }}>{row.note}</div>}
                </td>
                <td style={{ padding: "13px 14px", color: cellColor }}>{number(row.eligible)}</td>
                {isWeekly ? (
                  <>
                    <td style={{ padding: "13px 14px", color: cellColor }}>{number(row.present)}</td>
                    <td style={{ padding: "13px 14px", color: statusColor(row.attendancePct), fontWeight: T.fwBold }}>{pct(row.attendancePct)}</td>
                  </>
                ) : (
                  <>
                    <td style={{ padding: "13px 14px", color: cellColor }}>{muted ? "-" : row.status === "no_data" || row[thresholdField] == null ? "—" : row[thresholdField].toLocaleString()}</td>
                    <td style={{ padding: "13px 14px" }}><Status row={row} /></td>
                    {mode === "month" && (
                      <>
                        <td style={{ padding: "13px 14px", color: cellColor }}>{row.dailyAvgPresent == null ? "—" : row.dailyAvgPresent.toFixed(1)}</td>
                        <td style={{ padding: "13px 14px", color: cellColor }}>{row.dailyPeakPresent == null ? "—" : row.dailyPeakPresent.toLocaleString()}</td>
                      </>
                    )}
                    <td style={{ padding: "13px 14px" }}><Trend history={data.history || []} market={row.market} /></td>
                  </>
                )}
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {rows.length === 0 && <div style={{ padding: 24, color: T.light, textAlign: "center" }}>No {headlineClass} rows for this period.</div>}
    </div>
  );
}

function WorkerClasses({ data, mode }) {
  const field = mode === "month" ? "compliant10plus" : "compliant30plus";
  const headlineClass = EMPLOYEE;
  const grouped = useMemo(() => {
    return PERIOD_WORKER_CLASSES.map((workerClass) => {
      const classRows = (data?.rows || []).filter((row) => row.workerClass === workerClass);
      return {
        workerClass,
        eligible: classRows.reduce((sum, row) => sum + Number(row.eligible ?? 0), 0),
        met: classRows.reduce((sum, row) => sum + (["no_data", "coverage_gap"].includes(row.status) ? 0 : Number(row[field] ?? 0)), 0),
      };
    });
  }, [data, field]);
  return (
    <section data-testid="rto-worker-classes" style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, overflow: "hidden" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 5 }}>
        <div style={{ padding: "15px 18px 0", color: T.dark, fontWeight: T.fwBold, fontSize: T.fsMd5 }}>Worker classes</div>
        <span style={{ padding: "15px 18px 0", color: T.light, fontSize: T.fsXs, fontWeight: T.fwBold, textTransform: "uppercase", letterSpacing: ".05em" }}>Employee is the headline</span>
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: T.fsSm5, marginTop: 10 }}>
        <thead><tr style={{ color: T.light, textAlign: "left", fontSize: T.fsXs, textTransform: "uppercase", letterSpacing: ".04em" }}>
          <th style={{ padding: "10px 14px", borderBottom: `1px solid ${T.border}` }}>Worker class</th><th style={{ padding: "10px 14px", borderBottom: `1px solid ${T.border}` }}>Eligible</th><th style={{ padding: "10px 14px", borderBottom: `1px solid ${T.border}` }}>Met</th><th style={{ padding: "10px 14px", borderBottom: `1px solid ${T.border}` }}>Rate</th><th style={{ padding: "10px 14px", borderBottom: `1px solid ${T.border}` }}>Status</th>
        </tr></thead>
        <tbody>{grouped.map((row) => (
          <tr key={row.workerClass} data-testid={`rto-worker-class-${row.workerClass.replaceAll(" ", "-")}`} style={{ borderTop: `1px solid ${T.border}` }}>
            <td style={{ padding: "11px 14px", color: row.workerClass === headlineClass ? T.dark : T.mid, fontWeight: row.workerClass === headlineClass ? T.fwSemibold : T.fwRegular }}>{row.workerClass}</td>
            <td style={{ padding: "11px 14px", color: T.mid }}>{number(row.eligible)}</td>
            <td style={{ padding: "11px 14px", color: T.mid }}>{number(row.met)}</td>
            <td style={{ padding: "11px 14px", color: T.mid }}>{row.eligible ? pct(Math.min(100, (row.met / row.eligible) * 100)) : "n/a"}</td>
            <td style={{ padding: "11px 14px", color: row.workerClass === headlineClass ? T.green : T.light, fontWeight: T.fwSemibold }}>{row.workerClass === headlineClass ? "Headline" : "Reference"}</td>
          </tr>
        ))}</tbody>
      </table>
      <p style={{ margin: 0, padding: "12px 14px 15px", color: T.light, fontSize: T.fsXs, lineHeight: 1.5 }}>
         Employee is the headline class alongside Vendor Contractor and Independent Contractor. Only Employee contributes to the headline compliance result; the contractor classes are shown for context.
      </p>
    </section>
  );
}

function RtoComplianceListOwner({ onBack, prevReport = null, nextReport = null, onNavigateReport }) {
  const featureContext = useFeatureFlagContext();
  const eligibility = getReportNavEligibility(featureContext);
  const marketsEnabled = eligibility.markets && eligibility.offices;
  const [mode, setMode] = useState("month");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState("eligible");
  const [sortDirection, setSortDirection] = useState("desc");

  useEffect(() => {
    if (!eligibility.ready) {
      setData(null);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setData(null);
    setLoading(true);
    setError(null);
    const request = mode === "month" ? getRtoComplianceMonthly() : mode === "quarter" ? getRtoComplianceQuarterly() : getRtoComplianceWeekly();
    request.then((result) => { if (!cancelled) setData(result); }).catch((err) => { if (!cancelled) setError(err); }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [mode, eligibility.ready]);

  const changePeriod = (period) => {
    setLoading(true);
    setError(null);
    const request = mode === "month" ? getRtoComplianceMonthly(period) : mode === "quarter" ? getRtoComplianceQuarterly(period) : getRtoComplianceWeekly(period);
    request.then(setData).catch(setError).finally(() => setLoading(false));
  };

  const handleModeChange = (nextMode) => {
    setMode(nextMode);
    setSortKey("eligible");
    setSortDirection("desc");
  };
  const handleSort = (nextKey) => {
    if (nextKey === sortKey) {
      setSortDirection((current) => current === "desc" ? "asc" : "desc");
    } else {
      setSortKey(nextKey);
      setSortDirection("desc");
    }
  };

  const summary = data?.summary;
  const isWeekly = mode === "week";
  const weeklyHeadlineRows = (data?.rows || []).filter((row) =>
    row.workerClass === FULL_TIME && !["coverage_gap", "no_data"].includes(row.status)
  );
  const weeklyEligible = weeklyHeadlineRows.reduce((sum, row) => sum + (Number.isFinite(row.eligible) ? row.eligible : 0), 0);
  const weeklyPresent = weeklyHeadlineRows.reduce((sum, row) => sum + (Number.isFinite(row.present) ? row.present : 0), 0);
  const weeklyAttendancePct = weeklyEligible > 0 ? (weeklyPresent / weeklyEligible) * 100 : null;
  if (!eligibility.ready) {
    return <ReportAccessNotice reportName="RTO Compliance" loading onBack={onBack} recordNavigation={false} />;
  }

  return (
    <ReportShell
      onBack={onBack}
      prevReport={prevReport}
      nextReport={nextReport}
      onNavigateReport={onNavigateReport}
    >
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "24px 0 40px", width: "100%" }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ display: "inline-flex", background: T.lightGray, border: `1px solid ${T.border}`, borderRadius: 8, padding: 3 }}>
          {["month", "quarter", "week"].map((value) => <button key={value} type="button" data-testid={`rto-${value}-tab`} onClick={() => handleModeChange(value)} style={{ border: 0, borderRadius: 6, padding: "7px 12px", background: mode === value ? T.card : "transparent", color: mode === value ? T.dark : T.light, fontWeight: T.fwBold, cursor: "pointer" }}>{value === "month" ? "Monthly" : value === "quarter" ? "Quarterly" : "Weekly"}</button>)}
        </div>
        <PeriodControl mode={mode} data={data} onChange={changePeriod} />
      </div>
      {data && <div data-testid="rto-period-label" style={{ color: T.mid, fontSize: T.fsSm, margin: "-8px 0 14px" }}>
        {mode === "month" ? "Reporting month" : mode === "quarter" ? "Reporting quarter" : "Reporting week"}: {readableRtoPeriod(mode, data.period)}
        {data.isComplete === false ? " · partial" : " · complete"}
      </div>}
      {data && !data.isComplete && <div data-testid="rto-partial-warning" style={{ background: T.utilAmber, color: T.dark, border: `1px solid ${T.amber}`, borderRadius: 8, padding: "9px 12px", fontSize: T.fsSm, marginBottom: 14 }}>This is a partial period. It is selectable for inspection but does not represent a complete reporting period.</div>}
      {loading && <div data-testid="rto-loading" style={{ color: T.light, padding: 30, textAlign: "center" }}>Loading RTO compliance…</div>}
      {error && !loading && <div role="alert" style={{ color: T.red, padding: 30, textAlign: "center" }}>RTO compliance could not be loaded.</div>}
      {!loading && !error && data && (
        <>
          {!isWeekly && summary && (
            <>
              <div data-testid="rto-kpis" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10, marginBottom: 16 }}>
                <Kpi label="Eligible Employee" value={summary.eligible.toLocaleString()} detail="Employee is the headline class; Vendor Contractor and Independent Contractor are shown for context." />
                <Kpi label="Met threshold" value={summary.met.toLocaleString()} detail={mode === "month" ? "10+ days" : "30+ days"} />
                <Kpi label="Company compliance" value={pct(summary.compliancePct)} detail="Weighted by eligible people" tone={statusColor(summary.compliancePct)} />
                {marketsEnabled && <Kpi
                  label="Markets reporting"
                  value={`${summary.marketsReporting}/${summary.marketsTotal}`}
                  detail={[
                    summary.noDataMarkets ? `${summary.noDataMarkets} marked no data` : null,
                    summary.coverageGapMarkets ? `${summary.coverageGapMarkets} missing Workday headcount` : null,
                  ].filter(Boolean).join(" · ") || "All Employee markets reporting"}
                />}
              </div>
              {summary.observedZero && <div data-testid="rto-coverage-warning" style={{ color: T.amber, background: T.utilAmber, borderRadius: 8, padding: "9px 12px", fontSize: T.fsSm, marginBottom: 14 }}>Observed zero means Workday headcount was present and the measured result was 0%; it is not a closure or a missing Workday headcount.</div>}
            </>
          )}
          {isWeekly && !marketsEnabled && (
            <div data-testid="rto-weekly-company-summary" style={{ display: "flex", flexWrap: "wrap", gap: 14, padding: 18, marginBottom: 14, color: T.dark, background: T.card, border: `1px solid ${T.border}`, borderRadius: 10 }}>
              <strong>Full-time attendance</strong>
              <span>Present: {weeklyPresent.toLocaleString()} / {weeklyEligible.toLocaleString()} eligible</span>
              <span>Attendance rate: {weeklyAttendancePct == null ? "—" : pct(weeklyAttendancePct)}</span>
            </div>
          )}
          {marketsEnabled
            ? <MarketTable data={data} mode={mode} sortKey={sortKey} sortDirection={sortDirection} onSort={handleSort} />
            : <div data-testid="rto-breakdown-disabled" style={{ padding: 18, marginBottom: 14, color: T.mid, background: T.lightGray, borderRadius: 8 }}>
                Detailed breakdown is unavailable.
              </div>}
          {mode === "month" && <p style={{ color: T.light, fontSize: T.fsXs, lineHeight: 1.5, margin: "10px 2px" }}>{data.dailyPresenceLimitation}</p>}
          {!isWeekly && <WorkerClasses data={data} mode={mode} />}
        </>
      )}
      </div>
    </ReportShell>
  );
}